import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * CredentialsPage — P1-AI14 admin surface.
 *
 * Operator view of the nexus_credentials table (`/v1/admin/credentials`).
 * The page lets an operator:
 *
 *   - browse credentials (filtered by credential_type),
 *   - add a new credential (plaintext flows POST-only; server encrypts),
 *   - rotate a credential (plaintext PATCH; ownership flips to UI),
 *   - delete a credential.
 *
 * NEVER displays the stored value — only the 12-char fingerprint.
 * Plaintext lives in the modal form input ONLY at the moment of
 * create/rotate and is wiped on close.
 *
 *   ┌────── filters ─────── refresh · new ─────────────────────────────┐
 *   │ name · type · identity ref · fingerprint · owner · created · acts │
 *   └──────────────────────────────────────────────────────────────────┘
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ColHead, applyColumnFilters, compareBy } from '../components/tableKit.js';
import { NexusAdminApiError, useNexusFetch } from '../api.js';
import { CreateCredentialModal, emptyCredentialDraft, draftToCreateBody, draftToPatchBody, isValidCredentialName, CREDENTIAL_NAME_RE, } from './credentials/CreateCredentialModal.js';
// Friendly labels for the types the platform itself knows how to CREATE. Any
// other type that shows up in the rows (dexcom-share, outlook, oauth_grant,
// stripe_secret_key, …) is written by an app or a service, not this page — the
// dropdown and the Type column filter are built from what is actually there,
// so nothing can hide behind a hardcoded list again (Erwin, 2026-09-02).
const KNOWN_TYPE_LABELS = {
    llm_provider: 'LLM provider',
    broker: 'Broker',
    generic_api_token: 'Generic API token',
    aws_iot_cert: 'AWS IoT cert',
    smtp: 'SMTP',
    stripe_secret_key: 'Stripe secret key (payments)',
};
function typeLabel(t) {
    return KNOWN_TYPE_LABELS[t] ?? t;
}
// Column sort + filter (tableKit) — same kit as Issues / Users.
const CRED_COLS = ['name', 'type', 'identity', 'fingerprint', 'fields', 'owner', 'created'];
const CRED_ENUM_COLS = new Set(['type', 'owner']);
const EMPTY_CRED_CF = {
    name: [], type: [], identity: [], fingerprint: [], fields: [], owner: [], created: [],
};
export function CredentialsPage() {
    const fetch = useNexusFetch();
    const [credentials, setCredentials] = useState([]);
    const [sortCol, setSortCol] = useState('name');
    const [sortDir, setSortDir] = useState('asc');
    const [cf, setCf] = useState(EMPTY_CRED_CF);
    const [loading, setLoading] = useState(false);
    const [topBanner, setTopBanner] = useState(null);
    const [form, setForm] = useState(null);
    // ── Load ──────────────────────────────────────────────────────
    const load = useCallback(async () => {
        setLoading(true);
        setTopBanner(null);
        try {
            // Always the full list: the type dropdown and column filters are derived
            // from the rows, so a server-side type filter would hide the options.
            const resp = await fetch('/v1/admin/credentials');
            setCredentials(resp.credentials ?? []);
        }
        catch (e) {
            const msg = e instanceof NexusAdminApiError
                ? `${e.code}: ${e.message}`
                : e instanceof Error
                    ? e.message
                    : String(e);
            setTopBanner(msg);
        }
        finally {
            setLoading(false);
        }
    }, [fetch]);
    useEffect(() => {
        void load();
    }, [load]);
    // ── Modal helpers ─────────────────────────────────────────────
    const openCreate = useCallback(() => {
        setForm({
            mode: 'create',
            original: null,
            draft: emptyCredentialDraft(),
            fieldErrors: {},
            generalErrors: [],
            formBanner: null,
            submitting: false,
        });
    }, []);
    const openRotate = useCallback((row) => {
        // For rotate we pre-fill identityRef + type (immutable in the form)
        // and clear secrets — the operator types the new value.
        //
        // For smtp credentials, host/port/secure/from come from extraConfig
        // (NOT encrypted, returned in the wire shape) so we prefill them;
        // username + password reset to blank (operator must re-enter).
        const ec = (row.extraConfig ?? {});
        const isSmtp = row.credentialType === 'smtp';
        setForm({
            mode: 'edit',
            original: row,
            draft: {
                name: row.name,
                credentialType: row.credentialType,
                identityRef: row.identityRef ?? '',
                secretsText: '', // operator types new plaintext
                smtpUsername: isSmtp && typeof ec.username === 'string' ? ec.username : '',
                smtpPassword: '',
                smtpHost: isSmtp && typeof ec.host === 'string' ? ec.host : '',
                smtpPort: isSmtp && (typeof ec.port === 'number' || typeof ec.port === 'string')
                    ? String(ec.port)
                    : '587',
                smtpSecure: isSmtp && (ec.secure === true || ec.secure === 'true'),
                smtpFrom: isSmtp && typeof ec.from === 'string' ? ec.from : '',
                hostLoginHost: row.credentialType === 'host_login' && typeof ec.hostName === 'string' ? ec.hostName : '',
                hostLoginUsername: row.credentialType === 'host_login' && typeof ec.account === 'string' ? ec.account : '',
                hostLoginPassword: '',
                hostLoginPurpose: row.credentialType === 'host_login' && typeof ec.purpose === 'string' ? ec.purpose : 'sudo',
            },
            fieldErrors: {},
            generalErrors: [],
            formBanner: null,
            submitting: false,
        });
    }, []);
    const closeForm = useCallback(() => {
        setForm(null);
    }, []);
    const updateDraft = useCallback((key, value) => {
        setForm((prev) => (prev ? { ...prev, draft: { ...prev.draft, [key]: value } } : prev));
    }, []);
    // ── Submit (create/rotate) ────────────────────────────────────
    const onSubmit = useCallback(async (e) => {
        e.preventDefault();
        if (!form)
            return;
        setForm({ ...form, submitting: true, formBanner: null, fieldErrors: {}, generalErrors: [] });
        // Validate locally first.
        const draft = form.draft;
        const fieldErrors = {};
        if (form.mode === 'create') {
            if (!isValidCredentialName(draft.name.trim())) {
                fieldErrors.name = [`Must match ${CREDENTIAL_NAME_RE.source}.`];
            }
            if (!draft.credentialType) {
                fieldErrors.credentialType = ['Required'];
            }
        }
        // SMTP has dedicated fields validated inside assembleSmtpFields
        // (called by draftToCreate/PatchBody below); skip the generic
        // secretsText check for that type. P1-EM04.
        if (draft.credentialType !== 'smtp' && !draft.secretsText.trim()) {
            fieldErrors.secretsText = ['At least one apiKey or KEY=VALUE pair required'];
        }
        if (Object.keys(fieldErrors).length > 0) {
            setForm({ ...form, fieldErrors, submitting: false });
            return;
        }
        try {
            if (form.mode === 'create') {
                const body = draftToCreateBody(draft);
                if (!body.ok) {
                    setForm({
                        ...form,
                        fieldErrors: { secretsText: [body.error] },
                        submitting: false,
                    });
                    return;
                }
                await fetch(`/v1/admin/credentials`, {
                    method: 'POST',
                    json: body.body,
                });
            }
            else if (form.mode === 'edit' && form.original) {
                const body = draftToPatchBody(draft);
                if (!body.ok) {
                    setForm({
                        ...form,
                        fieldErrors: { secretsText: [body.error] },
                        submitting: false,
                    });
                    return;
                }
                await fetch(`/v1/admin/credentials/${form.original.id}`, {
                    method: 'PATCH',
                    json: body.body,
                });
            }
            setForm(null);
            await load();
        }
        catch (err) {
            const fEs = {};
            const generalErrors = [];
            let formBanner = null;
            if (err instanceof NexusAdminApiError) {
                formBanner = `${err.code}: ${err.message}`;
                if (Array.isArray(err.errors)) {
                    for (const fe of err.errors) {
                        const key = (fe.field ?? fe.path ?? '').replace(/^\/+/, '');
                        if (key in draft) {
                            const k = key;
                            fEs[k] = [...(fEs[k] ?? []), fe.message];
                        }
                        else {
                            generalErrors.push(fe.message);
                        }
                    }
                }
            }
            else {
                formBanner = err instanceof Error ? err.message : String(err);
            }
            setForm({
                ...form,
                fieldErrors: fEs,
                generalErrors,
                formBanner,
                submitting: false,
            });
        }
    }, [fetch, form, load]);
    // ── Delete ────────────────────────────────────────────────────
    const onDelete = useCallback(async (row) => {
        if (!window.confirm(`Delete credential "${row.name}"? The encrypted value cannot be recovered.`)) {
            return;
        }
        try {
            await fetch(`/v1/admin/credentials/${row.id}`, { method: 'DELETE' });
            await load();
        }
        catch (e) {
            const msg = e instanceof NexusAdminApiError
                ? `${e.code}: ${e.message}`
                : e instanceof Error
                    ? e.message
                    : String(e);
            setTopBanner(msg);
        }
    }, [fetch, load]);
    // ── Render ────────────────────────────────────────────────────
    const toggleSort = useCallback((colKey) => {
        const c = colKey;
        if (c === sortCol)
            setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        else {
            setSortCol(c);
            setSortDir('asc');
        }
    }, [sortCol]);
    const handleColSelected = useCallback((colKey, values) => {
        setCf((p) => ({ ...p, [colKey]: values }));
    }, []);
    const clearFilters = () => setCf(EMPTY_CRED_CF);
    const anyFilter = Object.values(cf).some((v) => v.length > 0);
    const cellVals = useCallback((r) => ({
        name: r.name,
        type: r.credentialType,
        identity: r.identityRef ?? '—',
        fingerprint: r.fingerprint,
        fields: r.secretFields.length ? r.secretFields.join(', ') : '—',
        owner: r.deployScriptManaged ? 'deploy' : 'ui',
        created: r.createdAt,
    }), []);
    // Every type actually present, plus the ones this page can create, so the
    // dropdown is complete even on an empty instance.
    const typeOpts = useMemo(() => {
        const seen = new Set(Object.keys(KNOWN_TYPE_LABELS));
        for (const r of credentials)
            seen.add(r.credentialType);
        return Array.from(seen).sort((a, b) => typeLabel(a).localeCompare(typeLabel(b)));
    }, [credentials]);
    const typeCounts = useMemo(() => {
        const m = new Map();
        for (const r of credentials)
            m.set(r.credentialType, (m.get(r.credentialType) ?? 0) + 1);
        return m;
    }, [credentials]);
    // The top dropdown is a shortcut for the Type column filter (single value).
    const dropdownValue = cf.type.length === 1 ? cf.type[0] : cf.type.length === 0 ? 'all' : 'multi';
    const visibleRows = useMemo(() => {
        const filtered = applyColumnFilters(credentials, cf, cellVals, CRED_ENUM_COLS);
        if (sortCol === 'created') {
            return [...filtered].sort(compareBy((r) => new Date(r.createdAt).getTime(), sortDir));
        }
        return [...filtered].sort(compareBy((r) => cellVals(r)[sortCol].toLowerCase(), sortDir));
    }, [credentials, cf, cellVals, sortCol, sortDir]);
    const head = (colKey, label, extra = {}) => (_jsx(ColHead, { colKey: colKey, label: label, sortCol: sortCol, sortDir: sortDir, onSort: toggleSort, selected: cf[colKey], onSelected: handleColSelected, ...extra }));
    return (_jsxs("div", { className: "container-fluid p-0", children: [_jsxs("div", { className: "d-flex align-items-center mb-3 flex-wrap gap-2", children: [_jsx("h3", { className: "mb-0", children: "Credentials" }), _jsx("span", { className: "text-muted small", children: "Encrypted secrets store. Plaintext stays in your hand for the seconds it takes to paste \u2014 server encrypts before it lands on disk." }), _jsxs("div", { className: "ms-auto d-flex gap-2", children: [anyFilter && (_jsxs("button", { type: "button", className: "btn btn-link btn-sm text-decoration-none", onClick: clearFilters, children: ["Clear filters (", visibleRows.length, "/", credentials.length, ")"] })), _jsxs("select", { className: "form-select form-select-sm", style: { width: 'auto' }, value: dropdownValue, onChange: (e) => {
                                    const v = e.target.value;
                                    handleColSelected('type', v === 'all' ? [] : v === 'multi' ? cf.type : [v]);
                                }, title: "Filter by credential type (same as the Type column filter)", children: [_jsxs("option", { value: "all", children: ["All types (", credentials.length, ")"] }), dropdownValue === 'multi' && _jsxs("option", { value: "multi", children: [cf.type.length, " types selected"] }), typeOpts.map((t) => (_jsxs("option", { value: t, children: [typeLabel(t), typeCounts.get(t) ? ` (${typeCounts.get(t)})` : ''] }, t)))] }), _jsx("button", { type: "button", className: "btn btn-outline-secondary btn-sm", onClick: () => void load(), disabled: loading, children: loading ? 'Refreshing…' : 'Refresh' }), _jsx("button", { type: "button", className: "btn btn-primary btn-sm", onClick: openCreate, children: "+ Add credential" })] })] }), topBanner && (_jsx("div", { className: "alert alert-danger", role: "alert", children: topBanner })), _jsx("div", { className: "table-responsive", children: _jsxs("table", { className: "table table-sm table-hover align-middle", children: [_jsx("thead", { children: _jsxs("tr", { children: [head('name', 'Name', { filterable: true, mode: 'text', placeholder: 'name contains…' }), head('type', 'Type', { filterable: true, mode: 'enum', options: typeOpts }), head('identity', 'Identity ref', { filterable: true, mode: 'text' }), head('fingerprint', 'Fingerprint', { filterable: true, mode: 'text' }), head('fields', 'Fields', { filterable: true, mode: 'text' }), head('owner', 'Owner', { filterable: true, mode: 'enum', options: ['deploy', 'ui'] }), head('created', 'Created'), _jsx("th", { className: "text-end", style: { position: 'sticky', top: 0, zIndex: 3, background: '#f1f3f5' }, children: "Actions" })] }) }), _jsxs("tbody", { children: [visibleRows.length === 0 && (_jsx("tr", { children: _jsx("td", { colSpan: 8, className: "text-center text-muted py-4", children: loading ? 'Loading…' : anyFilter ? 'No credentials match the current filters.' : 'No credentials yet — click + Add to create one.' }) })), visibleRows.map((row) => (_jsxs("tr", { children: [_jsx("td", { className: "font-monospace", children: row.name }), _jsx("td", { children: _jsx("span", { className: "badge bg-secondary", title: typeLabel(row.credentialType), children: row.credentialType }) }), _jsx("td", { className: "text-muted small", children: row.identityRef ?? '—' }), _jsx("td", { children: _jsx("code", { className: "small", children: row.fingerprint }) }), _jsx("td", { className: "small", children: row.secretFields.length === 0
                                                ? '—'
                                                : row.secretFields.join(', ') }), _jsx("td", { children: row.deployScriptManaged ? (_jsx("span", { className: "badge bg-info text-dark", children: "deploy" })) : (_jsx("span", { className: "badge bg-success", children: "ui" })) }), _jsx("td", { className: "small text-muted", children: new Date(row.createdAt).toLocaleString() }), _jsxs("td", { className: "text-end", children: [_jsx("button", { type: "button", className: "btn btn-outline-secondary btn-sm me-2", onClick: () => openRotate(row), children: "Rotate" }), _jsx("button", { type: "button", className: "btn btn-outline-danger btn-sm", onClick: () => void onDelete(row), children: "Delete" })] })] }, row.id)))] })] }) }), form && (_jsx(CreateCredentialModal, { mode: form.mode, original: form.original, draft: form.draft, fieldErrors: form.fieldErrors, generalErrors: form.generalErrors, formBanner: form.formBanner, submitting: form.submitting, onChange: updateDraft, onCancel: closeForm, onSubmit: onSubmit }))] }));
}
//# sourceMappingURL=CredentialsPage.js.map